import React, { useState } from 'react';
import { Calendar, Clock, MapPin, FileText, Users, PartyPopper, Briefcase, Dumbbell, DollarSign, Save, MessageCircle } from 'lucide-react';

const EVENT_TYPES = [
  { id: 'social', label: 'Social Event', icon: PartyPopper, color: 'pink', description: 'Team dinners, celebrations, parties' },
  { id: 'meeting', label: 'Meeting', icon: Briefcase, color: 'blue', description: 'Team meetings, AGMs, planning sessions' },
  { id: 'training', label: 'Training', icon: Dumbbell, color: 'green', description: 'Practice sessions, fitness training' },
  { id: 'fundraiser', label: 'Fundraiser', icon: DollarSign, color: 'yellow', description: 'Fundraising events, charity matches' },
  { id: 'other', label: 'Other', icon: Calendar, color: 'purple', description: 'Any other club event' }
];

const EditEvent = ({ event, onBack, onSave }) => {
  const [eventName, setEventName] = useState(event.name || '');
  const [eventType, setEventType] = useState(event.eventType || 'social');
  const [eventDate, setEventDate] = useState(event.date || '');
  const [eventTime, setEventTime] = useState(event.time || '');
  const [eventEndTime, setEventEndTime] = useState(event.endTime || '');
  const [eventVenue, setEventVenue] = useState(event.venue || '');
  const [eventDescription, setEventDescription] = useState(event.description || '');
  const [whatsappGroupLink, setWhatsappGroupLink] = useState(event.whatsappGroupLink || '');
  const [inviteAllMembers, setInviteAllMembers] = useState(event.inviteAllMembers !== false);
  const [allowGuests, setAllowGuests] = useState(event.allowGuests !== false);
  const [askMealPreference, setAskMealPreference] = useState(event.askMealPreference || false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!eventName.trim()) {
      alert('Please enter an event name');
      return;
    }
    if (!eventDate) {
      alert('Please select an event date');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...event,
        name: eventName.trim(),
        eventType,
        date: eventDate,
        time: eventTime || null,
        endTime: eventEndTime || null,
        venue: eventVenue.trim() || null,
        description: eventDescription.trim() || null,
        whatsappGroupLink: whatsappGroupLink.trim() || null,
        inviteAllMembers,
        allowGuests,
        askMealPreference,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error updating event');
    } finally {
      setSaving(false);
    }
  };

  // Count existing RSVPs
  const rsvpCount = Object.keys(event.rsvps || {}).length;

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onBack} className="text-gray-600 mb-4 hover:text-gray-800">
        ← Back to Events
      </button>

      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-100 rounded-full">
            <Calendar className="text-blue-600" size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Edit Event</h2>
            <p className="text-gray-600">Update event details</p>
          </div>
        </div>

        {/* RSVP Warning */}
        {rsvpCount > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 font-medium">
              ⚠️ This event has {rsvpCount} RSVP{rsvpCount !== 1 ? 's' : ''}.
              Changing the date or time will notify attendees.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {/* Event Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Event Type *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {EVENT_TYPES.map(type => {
                const IconComponent = type.icon;
                const isSelected = eventType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => setEventType(type.id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${isSelected
                        ? type.color === 'pink' ? 'border-pink-500 bg-pink-50' :
                          type.color === 'blue' ? 'border-blue-500 bg-blue-50' :
                            type.color === 'green' ? 'border-green-500 bg-green-50' :
                              type.color === 'yellow' ? 'border-yellow-500 bg-yellow-50' :
                                'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <IconComponent
                      size={24}
                      className={
                        isSelected
                          ? type.color === 'pink' ? 'text-pink-600' :
                            type.color === 'blue' ? 'text-blue-600' :
                              type.color === 'green' ? 'text-green-600' :
                                type.color === 'yellow' ? 'text-yellow-600' :
                                  'text-purple-600'
                          : 'text-gray-400'
                      }
                    />
                    <p className={`font-semibold mt-2 ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                      {type.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Event Name *
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Season End Party, Annual General Meeting"
            />
          </div>

          {/* Date and Time Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-1" />
                Date *
              </label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Start Time
              </label>
              <input
                type="time"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                End Time
              </label>
              <input
                type="time"
                value={eventEndTime}
                onChange={(e) => setEventEndTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Venue */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <MapPin size={16} className="inline mr-1" />
              Venue / Location
            </label>
            <input
              type="text"
              value={eventVenue}
              onChange={(e) => setEventVenue(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., The Crown Pub, 123 Main Street"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FileText size={16} className="inline mr-1" />
              Description
            </label>
            <textarea
              value={eventDescription}
              onChange={(e) => setEventDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Tell members what this event is about..."
            />
          </div>

          {/* WhatsApp Group Link */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <MessageCircle size={16} className="inline mr-1 text-green-600" />
              WhatsApp Group Link
            </label>
            <input
              type="url"
              value={whatsappGroupLink}
              onChange={(e) => setWhatsappGroupLink(e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${whatsappGroupLink && !whatsappGroupLink.startsWith('https://chat.whatsapp.com/')
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300'
                }`}
              placeholder="https://chat.whatsapp.com/..."
            />
            {whatsappGroupLink && !whatsappGroupLink.startsWith('https://chat.whatsapp.com/') && (
              <p className="text-xs text-red-600 mt-1">
                ⚠️ WhatsApp links should start with "https://chat.whatsapp.com/"
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              💡 Create a group for this event: WhatsApp → Group → Group info → Invite via link → Copy link
            </p>
          </div>

          {/* Options */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-gray-700 flex items-center gap-2">
              <Users size={18} />
              Invite Options
            </h4>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={inviteAllMembers}
                onChange={(e) => setInviteAllMembers(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-700">Invite all club members</span>
                <p className="text-sm text-gray-500">All registered players will be invited to RSVP</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={allowGuests}
                onChange={(e) => setAllowGuests(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-700">Allow guests</span>
                <p className="text-sm text-gray-500">Members can bring additional guests</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={askMealPreference}
                onChange={(e) => setAskMealPreference(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-700">Ask for meal preference</span>
                <p className="text-sm text-gray-500">Players can select Veg/Non-Veg when RSVPing</p>
              </div>
            </label>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !eventName.trim() || !eventDate}
            className={`w-full py-4 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2 ${saving || !eventName.trim() || !eventDate
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;